import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import $ from 'jquery';
import { initMetaLightbox } from './metacore/meta-lightbox.js';
import LazyLoad from 'vanilla-lazyload';

// Make GSAP and plugins globally available
if (!window.gsap) window.gsap = gsap;
if (!window.ScrollTrigger) window.ScrollTrigger = ScrollTrigger;
if (!window.ScrollToPlugin) window.ScrollToPlugin = ScrollToPlugin;
if (!window.$) window.$ = window.jQuery = $;
if (!window.LazyLoad) window.LazyLoad = LazyLoad;

// Register plugins
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

// Create a namespace for our engine
window.R24Engine = window.R24Engine || {};

// HELLO
const consolehead = [
  'color: white',
  'background: #ff5533',
  'font-size: 20px',
  'padding: 0.25em'
].join(';');
const consoletxt = [
    'color: white',
    'background: #ff5533',
    'font-size: 12px',
    'padding: 0.25em'
].join(';');

const message = 'MADE WITH ❤️ BY R24';
const message2 = 'For questions and feedback, contact: engine@relaunch24.de';

console.log('%c%s', consolehead, message);
console.log('%c%s', consoletxt, message2);

///////////////////////////////////////////////
// READY FOR STARTING THE ENGINE (VANILLA)
/////////////////////////////////////////////

async function init() {
  "use strict";

  // Performance tracking
  performance.mark('meta-engine-start');

  // Array to collect all imports
  const coreImports = [];

  ////////////////////////////////////////////////
  // IMPORT SCROLL
  ///////////////////////////////////////////////
  coreImports.push(import('./metacore/meta-scroll.js').then(module => {
    module.initMetaScroll();
  }));

  ////////////////////////////////////////////////
  // IMPORT DYNAMIC NAV OR SIDEBAR NAV
  ///////////////////////////////////////////////
  if (document.querySelector(".js-site-nav")) {  
    coreImports.push(import('./metacore/meta-nav-dynamic.js').then(module => {
      module.initDynamicNav();
    }));
  }

  if (document.querySelector(".meta-nav-sidebar")) {  
    coreImports.push(import('./metacore/meta-nav-sidebar.js').then(module => {
      module.initSidebarNav();
    }));
  }

  ////////////////////////////////////////////////
  // IMPORT COOKIES
  ///////////////////////////////////////////////
  if (document.querySelector("#footersettings") || document.querySelector("#r24ConsentBanner") || document.querySelector(".cookie-settings-button")) {
    coreImports.push(import('./metacore/meta-cookies.js').then(module => {
      module.initMetaCookies();
    }));
  }

  ////////////////////////////////////////////////
  // IMPORT PIRSCH EVENTS
  ///////////////////////////////////////////////
  if (document.querySelector('[data-pirsch-event]')) {
    coreImports.push(import('./metacore/meta-pirsch.js').then(module => {
      module.initMetaPirschEvents();
    }));
  }

  ////////////////////////////////////////////////
  // IMPORT BLOCK MODULES
  ///////////////////////////////////////////////
  
  const heroBoldEmbeddedVideo = document.querySelector('.section.mtkrft.herobold video.herobold-video');
  const heroAltEmbeddedVideo = document.querySelector('.section.mtkrft.heroalternate video.heroalt-assets-video, .section.mtkrft.landing video.heroalt-assets-video');
  const westbridgeHeroVideo = document.querySelector('.section.mtkrft.custom-hero-westbridge video.westbridge-hero-video');
  const ctaEmbeddedVideo = document.querySelector('.section.mtkrft.cta video.cta-bg-video');
  const numbersEmbeddedVideo = document.querySelector('.section.mtkrft.numbers video.numbers-bg-video');

  if (heroBoldEmbeddedVideo || heroAltEmbeddedVideo || westbridgeHeroVideo || ctaEmbeddedVideo || numbersEmbeddedVideo) {
    coreImports.push(import('./metablocks/hero.js').then(module => {
      if (heroBoldEmbeddedVideo) module.initHeroboldBlockVideo();
      if (heroAltEmbeddedVideo) module.initHeroaltEmbeddedVideos();
      if (westbridgeHeroVideo) module.initWestbridgeHeroVideo();
      if (ctaEmbeddedVideo) module.initCtaBlockVideos();
      if (numbersEmbeddedVideo) module.initNumbersBlockVideos();
    }));
  }

  // Raster-Slideshows (Hero/Content, mehrere Bilder)
  if (document.querySelector('[data-r24-slideshow]')) {
    coreImports.push(import('./metablocks/asset_slideshow.js').then(module => {
      module.initAssetSlideshows();
    }));
  }

  // Content slideshow (slider_slideshow)
  if (document.querySelector('[data-r24-content-slideshow]')) {
    coreImports.push(import('./metablocks/slider_slideshow.js').then(module => {
      module.initContentSlideshows();
    }));
  }

  // Forms
  if (document.querySelector('form')) {
    coreImports.push(import('./metablocks/misc_form.js').then(module => {
      module.initForm();
    }));
  }

  // Accordions
  if (document.querySelector('.faq6_accordion')) {
    coreImports.push(import('./metablocks/misc_accordion.js').then(module => {
      module.initAccordion();
    }));
  }

  // Numbers
  if (document.querySelector('.text-numbers')) {
    coreImports.push(import('./metablocks/misc_numbers.js').then(module => {
      module.initNumbers();
    }));
  }

  // Quantrefy Grid
  if (document.querySelector('.grid-card-quantrefy')) {
    coreImports.push(import('./metablocks/klients/quantrefy.js').then(module => {
      module.initQuantrefy();
    }));
  }

  // Landing share dropdown
  if (document.querySelector('.landing-share')) {
    coreImports.push(import('./metablocks/landing-share.js').then(module => {
      module.initLandingShare();
    }));
  }

  // Wait for all core imports to complete
  await Promise.all(coreImports);

  //////////////////////////////////
  // PRELOADS
  /////////////////////////////////

  // @all preload-backup (FadeOut)
  setTimeout(function() {
    const transitions = document.querySelectorAll('.transition');
    transitions.forEach(function(transition) {
      // Using GSAP for fade-out effect
      gsap.to(transition, {
        opacity: 0,
        duration: 0.33,
        onComplete: function() {
          transition.remove();
        }
      });
    });
  }, 300);

    /////////////////////////////////////////////////
  // UI FIXES 
  /////////////////////////////////////////////////


  // @all immer der zweiten section die id 'start' geben
  const sections = document.querySelectorAll("section");
  if (sections.length > 0) {
    // Check if 'start' ID already exists
    const existingStart = document.getElementById('start');
    if (!existingStart) {
      const targetSection = sections.length > 1 ? sections[1] : sections[0];
      // Only set ID if the section doesn't already have one
      if (!targetSection.id) {
        targetSection.id = "start";
      }
    }
  }


  // Force Blank Tab on Textarea-Links
  // Check if the .content-richtext class is present
  if (document.querySelector('.content-richtext')) {
    // Get the current site's origin (protocol + domain)
    var currentOrigin = window.location.origin;
    
    // Apply the target attribute only for external links inside .content-richtext
    document.querySelectorAll('.content-richtext a').forEach(link => {
      const href = link.getAttribute('href');
      
      // Check if the link is external (i.e., its origin is different from the current one)
      // Also check if it's not a mailto: or tel: link
      if (href && !href.startsWith(currentOrigin) && (href.startsWith('http') || href.startsWith('//')) && !href.startsWith('mailto:') && !href.startsWith('tel:')) {
        link.setAttribute('target', '_blank');
      }
    });
    
    // Apply target="_blank" for all PDF links, regardless of whether they are internal or external
    // Also add the 'richlink' class to PDFs --R24 disabled for now
    document.querySelectorAll('.content-richtext a[href$=".pdf"]').forEach(link => {
      link.setAttribute('target', '_blank'); // Open in new tab
    });
  }




  /////////////////////////////////////////////////
  // CUSTOM RUBBER-BAND EFFECT
  /////////////////////////////////////////////////

    // Simple rubber band effect
    function initRubberBand() {
      const dummy = document.getElementById('rubber-top-dummy');
      const mainElement = document.querySelector('main');
      
      if (!dummy) return;
      
      // Determine which element to animate based on main class
      let rubberNav;
      if (mainElement && mainElement.classList.contains('meta-main-sidebar')) {
        // For sidebar layout, target a different element
        rubberNav = document.querySelector('.meta-nav-sidebar-mobile');
      } else {
        // For regular layout, target the background
        rubberNav = document.querySelector('.meta-header-background');
      }
      
      if (!rubberNav) return;

      // Rubber band effect with ScrollTrigger - only on mobile portrait
      let isRubberBandActive = false;

      // Rubber top line
      const rubberTopLine = document.getElementById('rubber-top-line');
    
      
      
      // Use matchMedia to only apply on mobile portrait
      let mm = gsap.matchMedia();
      
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        gsap.to(dummy, {
          scrollTrigger: {
            trigger: dummy,
            start: "top top",
            end: "+=1",
            toggleActions: "play none none reset",
            onEnterBack: () => {
              if (!isRubberBandActive) {
                isRubberBandActive = true;
                
                
                // Sync header margin-top with rubber band
                gsap.fromTo(rubberNav,
                  { y: 0 },
                  {
                    y: 3,
                    duration: 0.25,
                    ease: "expo.out",
                    onComplete: () => {
                      gsap.to(rubberNav, {
                        y: 0,
                        duration: 0.25,
                        delay: 0,
                        ease: "circ.out",
                        onComplete: () => {
                          isRubberBandActive = false;
                        }
                      });
                    }
                  }
                );

                // Animate the rubber top line
                gsap.fromTo(rubberTopLine,
                  { 
                    scaleY: 0,
                    display: "block"
                  },
                  {
                    scaleY: 1,
                    duration: 0.25,
                    ease: "expo.out",
                    onComplete: () => {
                      gsap.to(rubberTopLine, {
                        scaleY: 0,
                        duration: 0.25,
                        ease: "circ.out",
                        onComplete: () => {
                          rubberTopLine.style.display = 'none';
                        }
                      });
                    }
                  }
                );
              }
            },
            markers: false
          }
        });
      });

    }

  // Initialize
  initRubberBand();


  //////////////////////////////////
  // VIDEO CONTROLLER (VANILLA)
  //////////////////////////////////

  function fadePosterBehindHeroVideo(player) {
    let posterImg = null;
    const next = player.nextElementSibling;
    if (next && next.tagName === 'PICTURE') {
      posterImg = next.querySelector('img');
    }
    if (!posterImg && player.parentElement) {
      posterImg = player.parentElement.querySelector('img.hero-bold-img, img.heroalt-assets-img, img.cta-video-poster, img.numbers-video-poster, img.custom-hero-westbridge-img');
    }
    if (!posterImg || posterImg.style.display === 'none') return;

    posterImg.style.transition = posterImg.style.transition || 'opacity 1s ease-out';
    posterImg.style.opacity = '0';
    setTimeout(() => {
      posterImg.style.display = 'none';
    }, 1000);
  }

  // Get all video controller instances
  const vidcons = document.querySelectorAll('.vidcon');

  // Only run if video controllers are found
  if (vidcons.length > 0) {
    vidcons.forEach(vidcon => {
      const vidconbtn = vidcon.querySelector('.vidcon-btn');
      const player = vidcon.nextElementSibling; // Get the video element that follows the vidcon

      if (!vidconbtn || !player || player.tagName !== 'VIDEO') return; // Skip if required elements aren't found

      // Play/Pause functionality
      vidconbtn.addEventListener('click', () => {
        try {
          if (player.paused) {
            const playPromise = player.play();
            if (playPromise !== undefined && typeof playPromise.then === 'function') {
              playPromise.then(() => {
                vidconbtn.setAttribute('data-playing', 'true');
                fadePosterBehindHeroVideo(player);
              }).catch(error => {
                console.log("Playback failed:", error);
              });
            } else {
              vidconbtn.setAttribute('data-playing', 'true');
              fadePosterBehindHeroVideo(player);
            }
          } else {
            player.pause();
            vidconbtn.setAttribute('data-playing', 'false');
          }
        } catch (e) {
          console.log("Video control error:", e);
        }
      });

    });
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.querySelectorAll('.heroalternate video.heroalt-assets-video, .section.mtkrft.landing video.heroalt-assets-video, .section.mtkrft.herobold video.herobold-video, .section.mtkrft.custom-hero-westbridge video.westbridge-hero-video, .section.mtkrft.cta video.cta-bg-video, .section.mtkrft.numbers video.numbers-bg-video').forEach((el) => {
      try {
        el.removeAttribute('autoplay');
        el.pause();
      } catch (e) {
        /* ignore */
      }
    });
  }



    


  //////////////////////////////////////////////////
  // LIGHTBOX ENGINE
  //////////////////////////////////////////////////

  if (document.querySelector('a.gallery-card[href], a.herogallery-asset-link[href], figure a[href]')) {
    initMetaLightbox();
  }

  ///// Gallery zoom hint (magnifying glass icon, top-right) /////
  function initAltTextBoxReveal() {
    const links = document.querySelectorAll(
      'figure a[href][aria-haspopup="dialog"]'
    );

    links.forEach(link => {
      if (link.classList.contains('gallery-card')) return;

      const overlay = link.querySelector('.hero-gallery-text, .gallery-text');
      if (!overlay) return;

      const icon = overlay.querySelector('.captionicon');
      if (!icon) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const inMasonry = link.closest('.gallery-cards--masonry') !== null;

      if (reducedMotion) {
        gsap.set(overlay, { opacity: 0 });
        const show = () => { gsap.set(overlay, { opacity: 1 }); };
        const hide = () => { gsap.set(overlay, { opacity: 0 }); };
        link.addEventListener('mouseenter', show);
        link.addEventListener('mouseleave', hide);
        link.addEventListener('focusin', show);
        link.addEventListener('focusout', hide);
        return;
      }

      // Fade the badge, scale only the icon — scaling the whole flex overlay hid the SVG.
      // A translate on the card itself makes CSS columns rebalance under the cursor.
      gsap.set(overlay, { opacity: 0 });
      gsap.set(icon, { scale: 0.5, transformOrigin: '100% 0%' });
      if (!inMasonry) {
        gsap.set(link, { y: 0 });
      }

      const show = () => {
        gsap.to(overlay, { opacity: 1, duration: 0.15, ease: 'power1.out' });
        gsap.to(icon, { scale: 1, duration: 0.32, ease: 'back.out(1.7)' });
        if (!inMasonry) {
          gsap.to(link, { y: -4, duration: 0.38, ease: 'power2.out' });
        }
      };
      const hide = () => {
        gsap.to(overlay, { opacity: 0, duration: 0.18, ease: 'power2.in' });
        gsap.to(icon, { scale: 0.5, duration: 0.22, ease: 'power2.in' });
        if (!inMasonry) {
          gsap.to(link, { y: 0, duration: 0.3, ease: 'power2.out' });
        }
      };

      link.addEventListener('mouseenter', show);
      link.addEventListener('mouseleave', hide);
      link.addEventListener('focusin', show);
      link.addEventListener('focusout', hide);
    });
  }
  initAltTextBoxReveal();



  /////////////////////////////////////////////////
  // GLOBAL COUNT-UP ANIMATION (GSAP) FUNCTION!
  /////////////////////////////////////////////////

    // Function to animate counting
    function animateCount(element, targetValue, duration = 1) {
        const startValue = 0;
        const startTime = performance.now();
        
        function updateCount(currentTime) {
            const elapsedTime = currentTime - startTime;
            const progress = Math.min(elapsedTime / (duration * 1000), 1);
            
            // Ease out function for smoother animation
            const easeProgress = 1 - Math.pow(1 - progress, 3);
            
            const currentValue = Math.floor(startValue + (targetValue - startValue) * easeProgress);
            
            // Format number with thousands separator
            const formattedValue = currentValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            
            // Update the element's text content
            element.textContent = formattedValue;
            
            if (progress < 1) {
                requestAnimationFrame(updateCount);
            } else {
                // Ensure final value is exact
                element.textContent = targetValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            }
        }
        
        requestAnimationFrame(updateCount);
    }

    // Make function globally available
    window.animateCount = animateCount;

    // Check if there are any elements with data-animate-count
    if (document.querySelectorAll('[data-animate-count]').length > 0) {
        // Get all elements with data-animate-count
        const countElements = document.querySelectorAll('[data-animate-count]');
        
        // Reset initial state
        countElements.forEach(element => {
            element.textContent = '0';
        });

        // Create ScrollTrigger for the container
        ScrollTrigger.create({
            trigger: '[data-animate-count]',
            start: "top 80%",
            end: "bottom 20%",
            onEnter: () => {
                // Animate each element
                countElements.forEach(element => {
                    const targetValue = parseInt(element.getAttribute('data-animate-count'));
                    const duration = parseFloat(element.getAttribute('data-animate-duration')) || 1;
                    
                    if (!isNaN(targetValue)) {
                        animateCount(element, targetValue, duration);
                    }
                });
            },
            onLeaveBack: () => {
                // Reset state when scrolling back out
                countElements.forEach(element => {
                    element.textContent = '0';
                });
      }
    });
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.querySelectorAll('.heroalternate video.heroalt-assets-video, .section.mtkrft.landing video.heroalt-assets-video, .section.mtkrft.herobold video.herobold-video, .section.mtkrft.custom-hero-westbridge video.westbridge-hero-video, .section.mtkrft.cta video.cta-bg-video, .section.mtkrft.numbers video.numbers-bg-video').forEach((el) => {
      try {
        el.removeAttribute('autoplay');
        el.pause();
      } catch (e) {
        /* ignore */
      }
    });
  }


  ////////////////////////////////////////////////
  // TEXT HIGHLIGHT EFFECT (BASED ON CODROPS EXAMPLE 7)
  ///////////////////////////////////////////////
  
  class TextHighlightEffect {
    constructor(el) {
      // Validates the input element to ensure it's an HTML element.
      if (!el || !(el instanceof HTMLElement)) {
        throw new Error('Invalid element provided.');
      }

      this.highlightedElement = el;
      this.highlightedChars = this.highlightedElement.querySelectorAll('.char');
      this.animationDefaults = {
        duration: 0.2,
        ease: 'sine',
      };
      
      // Calls the method to set up the initial effect.
      this.initializeEffect();
    }
    
    // Sets up the initial effect on the provided element.
    initializeEffect() {
      // Scroll effect.
      this.scroll();
    }

    // Defines the scroll effect logic for the element.
    scroll() {
      ScrollTrigger.create({
          trigger: this.highlightedElement,
          start: 'top bottom',
          onEnter: () => this.animateChars(),
          onEnterBack: () => this.animateChars(),
          // Reset the character's state when scrolling back past the element
          onLeave: () => this.resetChars(),
          onLeaveBack: () => this.resetChars()
      });
    }

    animateChars() {
      gsap
      .timeline({defaults: this.animationDefaults})
      .fromTo(this.highlightedChars, {
        transformOrigin: '50% 80%',
        scaleY: 0
      }, { 
        stagger: pos => 0.2+0.05*pos,
        scaleY: 1
      })
      .fromTo(this.highlightedElement, {
        '--after-height': '0%',
        willChange: 'height'
      }, {
        duration: 0.7,
        ease: 'sine.inOut',
        '--after-height': getComputedStyle(this.highlightedElement).getPropertyValue('--after-height-final')
      }, '<');
    }

    resetChars() {
      gsap.killTweensOf([this.highlightedChars, this.highlightedElement]);
      gsap.set(this.highlightedElement, {
        '--after-height': '0%'
      });
    }
  }

  // Simple character splitting function with accessibility support
  function splitTextIntoChars(element) {
    const text = element.textContent;
    element.innerHTML = '';
    
    // Create a wrapper to preserve the original text for screen readers
    const wrapper = document.createElement('span');
    wrapper.className = 'char-wrapper';
    wrapper.setAttribute('aria-hidden', 'true');
    element.appendChild(wrapper);
    
    // Add original text as hidden content for screen readers
    const screenReaderText = document.createElement('span');
    screenReaderText.className = 'sr-only';
    screenReaderText.textContent = text;
    element.appendChild(screenReaderText);
    
    for (let i = 0; i < text.length; i++) {
      const char = document.createElement('span');
      char.className = 'char';
      char.textContent = text[i];
      char.style.display = 'inline-block';
      char.style.transformOrigin = '50% 80%';
      
      // Preserve spaces by adding a non-breaking space if it's a regular space
      if (text[i] === ' ') {
        char.innerHTML = '&nbsp;';
      }
      
      wrapper.appendChild(char);
    }
  }

  // Initialize text highlight effects
  function initTextHighlight() {
    // Find all elements with the text-highlight class
    const highlightedElements = document.querySelectorAll('.text-highlight');
    
    if (highlightedElements.length === 0) return;

    // Split text into characters and apply the highlight effect to each element
    highlightedElements.forEach(el => {
      splitTextIntoChars(el);
      new TextHighlightEffect(el);
    });

    console.log(`R24 Engine: Initialized ${highlightedElements.length} text highlight effects`);
  }

  // Initialize text highlight effects if elements exist
  if (document.querySelector(".text-highlight")) {
    initTextHighlight();
  }

  // Future: Easy to add more animation types
  // function initTextHighlight2() {
  //   const elements = document.querySelectorAll('.text-highlight-2');
  //   elements.forEach(el => {
  //     splitTextIntoChars(el);
  //     new TextHighlightEffect2(el);
  //   });
  // }

  // Make the class globally available
  window.TextHighlightEffect = TextHighlightEffect;
  window.initTextHighlight = initTextHighlight;




  //////////////////////////////////////////////////
  // GLOBAL LOGO SLIDER ANIMATION (FOOTER)
  ////////////////////////////////////////////////////

  function initPartnerLogoAnimation() {
    const logoContainers = document.querySelectorAll('[data-animate-logos]');
    
    logoContainers.forEach(container => {
      const logos = container.querySelectorAll('.foot-partner-logo-item');
      
      if (logos.length === 0) return;
      
      // If only one logo, just show it without animation
      if (logos.length === 1) {
        gsap.set(logos, { opacity: 1 });
        return;
      }
      
      // Set initial opacity to 0 and pointer-events to none for all logos
      gsap.set(logos, { opacity: 0, pointerEvents: 'none' });
      
      // Create timeline for fade in/out sequence
      const tl = gsap.timeline({ repeat: -1 });
      
      logos.forEach((logo, index) => {
        // Fade in current logo and enable pointer events
        tl.to(logo, {
          opacity: 1,
          pointerEvents: 'auto',
          duration: 0.6,
          ease: 'power2.out'
        });
        
        // Hold visible for 2 seconds
        tl.to({}, { duration: 2 });
        
        // Fade out current logo and disable pointer events
        tl.to(logo, {
          opacity: 0,
          pointerEvents: 'none',
          duration: 0.6,
          ease: 'power2.in'
        });
        
        // Small pause before next logo (except after last logo)
        if (index < logos.length - 1) {
          tl.to({}, { duration: 0.3 });
        }
      });
      
      // Pause before restarting the loop
      tl.to({}, { duration: 1 });
    });
  }


  //////////////////////////////////////////////////
  // GLOBAL SUPER HERO TEXT ANIMATION (JQUERY, WESTBRIDGE)
  ////////////////////////////////////////////////////

  class SuperheroAnimation {
    constructor(element) {
      this.element = element;
      this.words = element.getElementsByClassName('word');
      this.wordArray = [];
      this.currentWord = 0;
      this.wordChangeInterval = null;
      this.isVisible = false;

      if (this.words.length > 0) {
        this.init();
      }
    }

    init() {
      this.words[this.currentWord].style.opacity = 1;

      for (let i = 0; i < this.words.length; i++) {
        this.splitLetters(this.words[i]);
      }

      this.setupScrollTrigger();
    }

    changeWord() {
      if (!this.isVisible) return;

      const cw = this.wordArray[this.currentWord];
      const nextIndex = (this.currentWord === this.words.length - 1) ? 0 : this.currentWord + 1;
      const nw = this.wordArray[nextIndex];

      for (let i = 0; i < cw.length; i++) {
        this.animateLetterOut(cw, i);
      }

      for (let i = 0; i < nw.length; i++) {
        nw[i].className = 'letter behind';
        if (nw[0]?.parentElement) {
          nw[0].parentElement.style.opacity = 1;
        }
        this.animateLetterIn(nw, i);
      }

      this.currentWord = nextIndex;
    }

    animateLetterOut(cw, i) {
      setTimeout(() => {
        cw[i].className = 'letter out';
      }, i * 80);
    }

    animateLetterIn(nw, i) {
      setTimeout(() => {
        nw[i].className = 'letter in';
      }, 340 + (i * 80));
    }

    splitLetters(word) {
      const content = word.textContent;
      word.innerHTML = '';
      const letters = [];

      for (let i = 0; i < content.length; i++) {
        const letter = document.createElement('span');
        letter.className = 'letter';
        letter.innerHTML = content.charAt(i);
        word.appendChild(letter);
        letters.push(letter);
      }

      this.wordArray.push(letters);
    }

    setupScrollTrigger() {
      ScrollTrigger.create({
        trigger: this.element,
        start: "top 80%",
        end: "bottom 20%",
        onEnter: () => {
          this.isVisible = true;
          if (!this.wordChangeInterval) {
            this.changeWord();
            this.wordChangeInterval = setInterval(() => this.changeWord(), 3000);
          }
        },
        onLeave: () => {
          this.isVisible = false;
          clearInterval(this.wordChangeInterval);
          this.wordChangeInterval = null;
        },
        onEnterBack: () => {
          this.isVisible = true;
          if (!this.wordChangeInterval) {
            this.changeWord();
            this.wordChangeInterval = setInterval(() => this.changeWord(), 3000);
          }
        },
        onLeaveBack: () => {
          this.isVisible = false;
          clearInterval(this.wordChangeInterval);
          this.wordChangeInterval = null;
        }
      });
    }

    destroy() {
      clearInterval(this.wordChangeInterval);
      this.wordChangeInterval = null;
    }
  }

  // Initialize all superhero elements
  const superheroElements = document.querySelectorAll('[data-superhero]');
  const superheroInstances = [];

  superheroElements.forEach(element => {
    superheroInstances.push(new SuperheroAnimation(element));
  });

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    superheroInstances.forEach(instance => instance.destroy());
  });

  // Initialize partner logo animation
  initPartnerLogoAnimation();



      






  //
  //
  //
  //
  //
  // PERFORMANCE CHECK END
  //
  //
  //
  //
  //
  //
  //

  // Mark performance end
  performance.mark('meta-engine-end');
  performance.measure('meta-engine-duration', 'meta-engine-start', 'meta-engine-end');
  const duration = performance.getEntriesByName('meta-engine-duration')[0].duration;
  console.log('%c%s', consoletxt, `R24 Engine init in ${duration.toFixed(2)}ms`);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
